import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  notification,
  Tag,
  Form,
  message,
  Input,
  Upload,
  Button,
  Select,
  InputNumber,
  Modal
} from "antd";
import { PlusOutlined, FileOutlined, CopyOutlined } from "@ant-design/icons";
import axios from "axios";
import { useParams } from "react-router-dom";
import { formatDateTime } from "../../helpers/date";
import IssueStatusTag from "../shared/IssueStatusTag";
import { ISSUE_STATUSES, ISSUE_CATEGORIES, LOCKED_COMMENT_STATUSES } from "../../constants/issue";
import config from "../../config/app";
import { previewBase64Image } from "../../helpers/image";
import { usePermissions } from "../App/PermissionContext";
import { PERMISSIONS } from "../../constants/roles";

const routes = [
  { path: "", breadcrumbName: "App" },
  { path: "", breadcrumbName: "Support Tickets" },
  { path: "", breadcrumbName: "Ticket Details" },
];

const SupportTicketDetail = () => {
  const { permissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [editedCategory, setEditedCategory] = useState([]);
  const [originalCategory, setOriginalCategory] = useState([]);
  const [form] = Form.useForm();
  const { id } = useParams();
  const history = useHistory();

  const fetchTicket = useCallback(async () => {
    setLoading(true);

    try {
      const {
        data: { data: ticket },
      } = await axios.get(`/api/issues/${id}`);

      setTicket(ticket);
    } catch (error) {
      notification.error({
        message: `Error`,
        description: error.response
          ? error.response.data.message
          : error.message,
      });
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handleOpenAttachment = (issueId, attachment) => {
    axios
      .post(`/issues/${issueId}/attachment`, { attachment })
      .then((res) => window.open(res.data.data.url, "_blank"));
  };

  const handleCopyAttachmentLink = (issueId, attachment) => {
    const s3Key =
      attachment.type === "paymentAdvice"
        ? `${config.s3Folder.paymentAdvice}/${attachment.key || attachment.name}`
        : `${config.s3Folder.issueAttachment}/${issueId}/${attachment.key || attachment.name}`;

    axios
      .post(`api/storage/generate-public-link`, { s3Key, validDays: 90 })
      .then((res) => {
        navigator.clipboard.writeText(res.data.data.url);

        message.success(
          "Share Link generated and copied to clipboard (Valid for 90 days)",
          10
        );
      })
      .catch(() => {
        message.error("Error generating share link");
      });
  };

  const startEditingCategory = () => {
    const currentCategory = [ticket.category].flat().filter(Boolean);

    setOriginalCategory(currentCategory);
    setEditedCategory(currentCategory);
    setIsEditingCategory(true);
  };

  const categoryChangeHandler = (value) => {
    // Existing categories cannot be removed, only new ones can be added
    let merged = [...new Set([...originalCategory, ...value])];

    if (
      merged.includes(ISSUE_CATEGORIES.WEIGHT_DISPUTE_APPEAL.key) &&
      merged.includes(ISSUE_CATEGORIES.CLAIM.key)
    ) {
      const blocked = originalCategory.includes(
        ISSUE_CATEGORIES.WEIGHT_DISPUTE_APPEAL.key
      )
        ? ISSUE_CATEGORIES.CLAIM.key
        : ISSUE_CATEGORIES.WEIGHT_DISPUTE_APPEAL.key;

      message.error(
        blocked === ISSUE_CATEGORIES.CLAIM.key
          ? "Cannot add Claim category to a ticket with Weight Dispute Appeal"
          : "Cannot add Weight Dispute Appeal category to a ticket with Claim"
      );
      merged = merged.filter((c) => c !== blocked);
    }

    setEditedCategory(merged);
  };

  const saveCategoryHandler = () => {
    const newCategories = editedCategory.filter(
      (c) => !originalCategory.includes(c)
    );

    if (!newCategories.length) {
      setIsEditingCategory(false);
      return;
    }

    Modal.confirm({
      title: "Add category to this ticket?",
      content: `This will add the following category(ies): ${newCategories
        .map((c) => ISSUE_CATEGORIES[c]?.label || c)
        .join(", ")}. Existing categories cannot be removed.`,
      okText: "Add",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axios.patch(`/api/issues/${ticket?.objectId}/category`, {
            category: editedCategory,
          });

          setIsEditingCategory(false);
          message.success("Category updated");
          fetchTicket();
        } catch (error) {
          message.error(error.response?.data.message || error.message);
        }
      },
    });
  };

  const addCommentHandler = async (newComment) => {
    try {
      await axios.post(
        `/api/issues/${ticket?.objectId}/comments`,
        {
          ...newComment,
          attachments: uploadedFiles,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          formSerializer: {
            indexes: null,
          },
        }
      );

      form.resetFields();
      fetchTicket();
    } catch (error) {
      message.error(error.response?.data.message || error.message);
    }
  };

  return (
    <div>
      <div className="header">
        <Row>
          <Col span={12}>
            <h2 className="detail-header-text">Support Ticket</h2>
          </Col>
          <Col span={12}>
            <Breadcrumb
              style={{ margin: "10px 0", textAlign: "right" }}
              routes={routes}
            />
          </Col>
        </Row>
      </div>

      {ticket && (
        <div>
          <Row justify="center" align="middle">
            <Col span={12}>
              <Card
                title={
                  <span>
                    <strong>Ticket ID #{ticket.ticketId}</strong>
                  </span>
                }
                bordered={true}
              >
                <p>
                  <strong>Ticket ID: </strong>
                  <br />
                  <Tag color="blue">{ticket.ticketId}</Tag>
                </p>
                <p>
                  <strong>Submitted At: </strong>
                  <br />
                  {formatDateTime(ticket.createdAt)}
                </p>
                <p>
                  <strong>Submitted By: </strong>
                  <br />
                  {ticket?.createdBy?.email || "N/A"}
                </p>
                <p>
                  <strong>Tracking No: </strong>
                  <br />
                  {ticket.order?.consign_no}
                </p>
                <p>
                  <strong>User / Merchant: </strong>
                  <br />
                  {ticket.user?.email}
                </p>
                <p>
                  <strong>Bank Details: </strong>
                  <br />
                  {`${ticket?.merchant?.bankHolderName} (${ticket?.merchant?.bankName} ${ticket?.merchant?.bankAccount})`}
                </p>
                <p>
                  <strong>Assignee: </strong>
                  <br />
                  {ticket.assignedTo?.username || "N/A"}
                </p>
                <p>
                  <strong>Issue Category: </strong>
                  <br />
                  {!isEditingCategory && (
                    <>
                      {[ticket.category].flat().filter(Boolean).map((c) => (
                        <Tag key={c} color={ISSUE_CATEGORIES[c]?.color || "grey"}>
                          {ISSUE_CATEGORIES[c]?.label || c}
                        </Tag>
                      ))}
                      {permissions?.includes(PERMISSIONS.COMMENT_ISSUES) && (
                        <Button size="small" onClick={startEditingCategory}>
                          Edit
                        </Button>
                      )}
                    </>
                  )}
                  {isEditingCategory && (
                    <div style={{ marginTop: 8 }}>
                      <Select
                        mode="multiple"
                        placeholder="Select category..."
                        style={{ width: 300 }}
                        value={editedCategory}
                        onChange={categoryChangeHandler}
                        tagRender={({ value, label, closable, onClose }) => (
                          <Tag
                            color={ISSUE_CATEGORIES[value]?.color || "grey"}
                            closable={closable && !originalCategory.includes(value)}
                            onClose={onClose}
                            style={{ marginRight: 3 }}
                          >
                            {label}
                          </Tag>
                        )}
                      >
                        {Object.values(ISSUE_CATEGORIES)
                          .filter(
                            (category) =>
                              originalCategory.includes(category.key) ||
                              category.key === ISSUE_CATEGORIES.CLAIM.key
                          )
                          .map((category) => (
                            <Select.Option
                              key={category.key}
                              value={category.key}
                              disabled={
                                originalCategory.includes(category.key) ||
                                (category.key === ISSUE_CATEGORIES.CLAIM.key &&
                                  originalCategory.includes(
                                    ISSUE_CATEGORIES.WEIGHT_DISPUTE_APPEAL.key
                                  ))
                              }
                            >
                              {category.label}
                            </Select.Option>
                          ))}
                      </Select>
                      {` `}
                      <Button
                        size="small"
                        type="primary"
                        disabled={
                          !editedCategory.some((c) => !originalCategory.includes(c))
                        }
                        onClick={saveCategoryHandler}
                      >
                        Save
                      </Button>
                      {` `}
                      <Button
                        size="small"
                        onClick={() => setIsEditingCategory(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </p>
                <p>
                  <strong>Status: </strong>
                  <br />
                  <IssueStatusTag statusKey={ticket.status} />
                </p>
                <p>
                  <strong>Description: </strong>
                  <br />
                  <span className="whitespace-pre-line">{ticket.remark}</span>
                </p>
                <p>
                  <strong>Attachments: </strong>
                  <br />
                  <ol>
                    {ticket.attachments.map((attachment) => (
                      <li key={`${attachment.type}-${attachment.name}`}>
                        {attachment.name}
                        {" "}
                        <Button
                          size="small"
                          onClick={() =>
                            handleOpenAttachment(ticket.objectId, attachment)
                          }
                        >
                          <FileOutlined />
                          Open
                        </Button>
                        {" "}
                        <Button
                          icon={<CopyOutlined />}
                          size="small"
                          onClick={() => {
                            handleCopyAttachmentLink(
                              ticket.objectId,
                              attachment
                            )
                          }}
                        >
                          Copy Link
                        </Button>
                      </li>
                    ))}
                  </ol>
                </p>
                <p>
                  <strong>Claimable Amount: </strong>
                  <br />
                  <span className="whitespace-pre-line">RM {ticket.claimableAmount}</span>
                </p>
              </Card>
            </Col>
          </Row>

          <Row className="mt-2" justify="center" align="middle">
            <Col span={12}>
              <Card>
                {!ticket.comments?.length && "No comments added yet"}
                {ticket.comments.map((comment) => (
                  <Row>
                    <Col span={22} offset={comment.attendedBy ? 2 : 0}>
                      <Card
                        title={
                          <span>
                            {comment.attendedBy
                              ? `Admin (${comment.attendedBy?.username})`
                              : "User"}
                            <br />
                            <span className="text-xs">
                              {formatDateTime(comment.createdAt)}
                            </span>
                          </span>
                        }
                      >
                        <p className="whitespace-pre-line">{comment.remark}</p>
                        <p className="text-xs">Attachment:</p>
                        <Row>
                          {!comment.attachments?.length && "-"}

                          <ol>
                            {comment.attachments.map((attachment) => (
                              <li key={`${attachment.type}-${attachment.name}`}>
                                {attachment.name}
                                {" "}
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleOpenAttachment(comment.objectId, attachment)
                                  }
                                >
                                  <FileOutlined />
                                  Open
                                </Button>
                                {" "}
                                <Button
                                  icon={<CopyOutlined />}
                                  size="small"
                                  onClick={() => {
                                    handleCopyAttachmentLink(
                                      comment.objectId,
                                      attachment
                                    )
                                  }}
                                >
                                  Copy Link
                                </Button>
                              </li>
                            ))}
                          </ol>
                        </Row>
                        {comment.status && (
                          <div>
                            <p className="text-xs">Status:</p>
                            <IssueStatusTag statusKey={comment.status} />
                          </div>
                        )}
                      </Card>
                    </Col>
                  </Row>
                ))}
              </Card>
            </Col>
          </Row>

          {permissions?.includes(PERMISSIONS.COMMENT_ISSUES) &&
            !LOCKED_COMMENT_STATUSES.includes(ticket?.status) && (
              <Row className="mt-2" justify="center" align="middle">
                <Col span={12}>
                  <Card title="Add Comment">
                    <Form
                      form={form}
                      name="add-issue-comment-form"
                      autoComplete="off"
                      onFinish={addCommentHandler}
                    >
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, curr) => prev.status !== curr.status}
                      >
                        {({ getFieldValue }) => {
                          const status = getFieldValue("status");
                          const isAutoGenerated = status === "PENDING_PAYMENT";

                          return (
                            <Form.Item
                              label="Remark"
                              name="remark"
                              rules={
                                isAutoGenerated
                                  ? []
                                  : [{ required: true, message: "Remark is required" }]
                              }
                            >
                              <Input.TextArea
                                rows={4}
                                disabled={isAutoGenerated} // optional
                                placeholder={
                                  isAutoGenerated
                                    ? "Remark will be auto-generated"
                                    : "Enter remark"
                                }
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>

                      <Form.Item label="Attachments" name="attachments">
                        <Upload
                          beforeUpload={() => false}
                          listType="picture-card"
                          onChange={(e) => {
                            setUploadedFiles((prev) => [...prev, e.file]);
                          }}
                          onPreview={(file) => previewBase64Image(file)}
                        >
                          <div>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>Upload</div>
                          </div>
                        </Upload>
                      </Form.Item>

                      <Form.Item label="Status" name="status">
                        <Select
                          defaultValue={null}
                          style={{ width: 300 }}
                          placeholder="Ignore if no change"
                        >
                          {ISSUE_STATUSES.filter((s) => !s.isHidden).map((status) => (
                            <Select.Option value={status.key}>
                              {status.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>

                      {[ticket.category].flat().includes(ISSUE_CATEGORIES.CLAIM.key) && (
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, curr) => prev.status !== curr.status}
                        >
                          {({ getFieldValue }) => {
                            const status = getFieldValue("status");

                            if (status !== "PENDING_PAYMENT") return null;

                            return (
                              <Form.Item
                                label="Claimable Amount"
                                name="claimableAmount"
                                rules={[
                                  { required: true, message: "Claimable Amount is required" },
                                  {
                                    type: "number",
                                    min: 1,
                                    message: "Claimable Amount must be at least RM1",
                                  },
                                ]}
                              >
                                <InputNumber
                                  style={{ width: "100%" }}
                                  min={1}
                                  precision={2}
                                  step={0.01}
                                  placeholder="Enter claimable amount"
                                  formatter={(value) => (value ? `RM ${value}` : "")}
                                  parser={(value) =>
                                    value?.replace(/RM\s?|(,*)/g, "")
                                  }
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      )}

                      <Row justify="space-between">
                        <Button size="middle" onClick={() => history.goBack()}>
                          Back
                        </Button>

                        <Form.Item>
                          <Button
                            loading={loading}
                            type="primary"
                            htmlType="submit"
                          >
                            + Add
                          </Button>
                        </Form.Item>
                      </Row>
                    </Form>
                  </Card>
                </Col>
              </Row>
            )}
        </div>
      )}
    </div>
  );
};

export default SupportTicketDetail;
